import React, { useEffect } from "react";
import { SelectChangeEvent } from "@mui/material/Select";
import { useStore } from "../../store/store";
import { useRequiredMsgs } from "../../store/requiredMsgs";
import fractionToInt from "../../util/fractionToInt";

function useJambInputSelector(jamb: string, fraction: string) {
  const addToQuote = useStore((state) => state.addToQuote);

  const doorType = useStore((state) => state.quote.doorType);
  const exactConfig = useStore((state) => state.quote.exactConfig);
  const addScreen = useStore((state) => state.quote.addScreen);
  const screenType = useStore((state) => state.quote.screenType);

  const kerf = useStore((state) => state.quote.kerf);

  const findRequired = useRequiredMsgs((state) => state.findRequired);
  const removeRequired = useRequiredMsgs((state) => state.removeRequired);

  const handleErrMsgChange = useStore((state) => state.handleError);
  const errors = useStore((state) => state.errors);

  useEffect(() => {
    if (!jamb) return;

    const overall = fractionToInt(jamb, fraction);

    let min: { num: number; msg: string } = { num: 0, msg: "" };
    let max: { num: number; msg: string } = { num: 0, msg: "" };

    if (exactConfig === "Fixed Full Bound") {
      min = { num: 4, msg: '4"' };
      max = { num: 8.5, msg: '8 1/2"' };
    } else if (exactConfig === "Fixed Slider") {
      min = { num: 4.125, msg: '4 1/8"' };
      max = { num: 8.5, msg: '8 1/2"' };
    } else if (doorType === "Pivot") {
      min = { num: 5, msg: '5"' };
      max = { num: 9.5, msg: '9 1/2"' };
    } else if (doorType === "Swing") {
      if (addScreen && screenType === "Centor Flat Screen/Shade") {
        min = { num: 7.875, msg: '7 7/8"' };
        max = { num: 10.875, msg: '10 7/8"' };
      } else {
        min = addScreen ? { num: 6.375, msg: '6 3/8"' } : { num: 4, msg: '4"' };
        max = addScreen
          ? { num: 10.875, msg: '10 7/8"' }
          : { num: 8, msg: '8"' };
      }
    } else {
      if (addScreen) {
        if (screenType === "Centor Flat Screen/Shade") {
          min = kerf
            ? { num: 10.25, msg: '10 1/4"' }
            : { num: 9.875, msg: '9 7/8"' };
          max = kerf ? { num: 11, msg: '11"' } : { num: 10, msg: '10"' };
        } else {
          min = kerf
            ? { num: 8.875, msg: '8 7/8"' }
            : { num: 8.5, msg: '8 1/2"' };
          max = { num: 10, msg: '10"' };
        }
      } else {
        min = kerf ? { num: 6, msg: '6"' } : { num: 5.625, msg: '5 5/8"' };
        max = { num: 8.5, msg: '8 1/2"' };
      }
    }

    const errMessage = `Must be between ${min.msg} - ${max.msg}`;

    if (!addScreen && overall >= min.num && overall <= max.num) {
      handleErrMsgChange("jambErr", "");
    } else if (!addScreen && (overall < min.num || overall > max.num)) {
      handleErrMsgChange("jambErr", errMessage);
    } else if (addScreen && overall >= min.num && overall <= max.num) {
      handleErrMsgChange("jambErr", "");
    } else if (addScreen && (overall < min.num || overall > max.num)) {
      handleErrMsgChange("jambErr", errMessage);
    }
  }, [fraction, jamb, addScreen, doorType, kerf, exactConfig]);

  const handleChange = (event: React.ChangeEvent<{ value: string }>) => {
    const { value } = event.target;

    if (value) {
      const jambString = fraction ? `${value} ${fraction}` : value;
      addToQuote({ jamb: jambString }, "jamb");
    } else {
      const jambString = fraction ? ` ${fraction}` : "";
      addToQuote({ jamb: jambString }, "jamb");
    }

    if (findRequired("Jamb Width"))
      removeRequired("Jamb Width", "Custom Overall Jamb Width");
  };

  const handleChangeFraction = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    if (value) addToQuote({ jamb: `${jamb} ${value}` });
    else addToQuote({ jamb: `${jamb}` });
  };

  const upcharge =
    exactConfig === "Fixed Slider"
      ? '6-5/8"'
      : doorType === "Swing" && !addScreen
      ? '5-5/8"'
      : doorType === "Swing" &&
        addScreen &&
        screenType === "Centor Flat Screen/Shade"
      ? '7-7/8"'
      : doorType === "Swing" && addScreen
      ? '6-7/8"'
      : screenType === "Centor Flat Screen/Shade" && addScreen && !kerf
      ? '9-7/8"'
      : screenType === "Centor Flat Screen/Shade" && addScreen && kerf
      ? '10-1/4"'
      : !addScreen && !kerf
      ? '5-5/8"'
      : !addScreen && kerf
      ? '6"'
      : addScreen && !kerf
      ? '8-1/2"'
      : addScreen && kerf
      ? '8-7/8"'
      : null;

  return {
    upcharge,
    handleChangeFraction,
    handleChange,
    jambErr: errors.jambErr,
  };
};

export default useJambInputSelector;
